import { isGeoRestricted, showCopyrightVideo } from '../../lib/utils/common'
import dateFormat from '../../lib/utils/dateformat'
import selectn from 'selectn'
import moment from 'moment-timezone'
import { common } from '../../lib/transform/parsers/common'
/**
 * Filter out geo restricted videos on live tab - innings summary section.
 * This data is created from rostersNode.
 * Iterate over players and reset video object if necessary.
 *
 * @param  {Object} innings current innings details
 * @param  {String} country user's country
 * @param  {Object} copyRightOptions user's domain, site etc information
 * @return {undefined}           undefined
 */
export function filterActivePlayerVideos(innings = {}, country = '', copyRightOptions = {}) {
  // Do not filter out videos if country is not passed
  if (country === '') {
    return
  }
  // Get active players for this innings
  let activePlayers = innings.players || []
  // Active players are split in batsmen & bowlers
  let sections = [ 'batsmen', 'bowlers' ]
  // iterater over batsmen and bowlers sections of current innings
  for (let j = 0; j < sections.length; j++) {
    let section = sections[j]
    if (activePlayers.hasOwnProperty(section) === false) {
      continue
    }
    // extract players for current section
    let players = selectn(`[${section}].list`, activePlayers) || []
    //console.info('players', players)
    // Iterate over all players from this section & call reset fn
    for (let k = 0; k < players.length; k++) {
      let player = players[k] || {}
      //console.info('player', player)
      const video = player.video || {}

      //check for copyright video for a country and show/hide
      if(!showCopyrightVideo(video.ad, copyRightOptions)) {
        player.video = {}
        player.hasVideoId = false          
      }

      if(isGeoRestricted(video.geoRestrictions, country)) {
        player.video = {}
        player.hasVideoId = false
      }
    }
  }
}

/**
 * Filter out geo restricted videos on scorecard tab.
 * Scorecard tab has players data for each innings created from rostersNode.
 * Iterate over players and reset video object if necessary.
 *
 * @param  {Array}  scorecard scorecard page input data
 * @param  {String} country   country of the user
* @param   {Object} copyRightOptions user's domain, site etc information
 * @return {undefined}           undefined
 */
export function filterScorecardVideos(scorecard = {}, country = '', copyRightOptions = {}) {
  // Do not filter out videos if country is not passed
  if (country === '') {
    return
  }
  //console.info('scorecard', scorecard)

  // extract all innings
  let allInnings = scorecard.innings || {}
  //console.info('allInnings', allInnings)

  // Iterater over all innings
  for (let i in allInnings) {
    let current = allInnings[i] || {}
    let sections = [ 'batsmen', 'bowlers' ]
    // iterater over batsmen and bowlers sections of current innings
    for (let j = 0; j < sections.length; j++) {
      let section = sections[j]
      if (current.hasOwnProperty(section) === false) {
        continue
      }
      // extract players for current section
      let players = current[section] || []
      //console.info('scorecard players', players)
      // Iterate over all players from this section & call reset fn
      for (let k = 0; k < players.length; k++) {
        let player = players[k] || {}
        const video = player.video || {}
        
        //check for copyright video for a country and show/hide
        if(!showCopyrightVideo(video.ad, copyRightOptions)) {
          player.video = {}
          player.hasVideoId = false          
        }

        if(isGeoRestricted(video.geoRestrictions, country)) {
          player.video = {}
          player.hasVideoId = false
        }
      }
    }
  }
}

/**
 * Filter out geo restricted videos on commentary
 *
 * @param  {Object} commentary commentary object
 * @param  {String} country   country of the user
 * @param  {Object} copyRightOptions user's domain, site etc information
 * @return {undefined}
 */
export function filterCommentaryVideos(commentary = {}, country = '', copyRightOptions = {}) {
  (commentary.overs || []).map((over) => {
    const comments = over.comments
    comments.map((comment) => {
      const video = comment.video || {}

      //check for copyright video for a country and show/hide
      if(!showCopyrightVideo(video.ad, copyRightOptions)) {
        comment.video = {}
        comment.hasVideoId = false          
      }

      if(isGeoRestricted(video.geoRestrictions, country)) {
        comment.video = {}
        comment.hasVideoId = false
      }
    })
  })
}


/**
 * update new state of commentary
 * @param  {Object} state previous state
 * @param  {Object} data  action data
 * @param  {Boolean} isPolling Determine if commentary is polling
 * @param  {Object} copyRightOptions user's domain, site etc information
 * @return {Object}       next state
 */
export function updateCommentary(state = {}, data ={}, isPolling, copyRightOptions = {}) {
  const prevCommentary = selectn('fullCommentary', state) || {}
  const { oversByPage : preOversByPage = {}, page: prevPage } = prevCommentary
  let fullCommentary = selectn('fullCommentary', data) || {}
  let { oversByPage = {}, page, pageCount, overs = [] } = fullCommentary
  let allOvers = []

  // reverse order the comments
  overs.map((over = {}) => {
    let comments = over.comments
    over.comments = (comments.length > 0) && comments.reverse()
  })

  // retain page from prev state if commentary is polling
  if (isPolling) {
    fullCommentary.page = page = prevPage
  }

  // more commentary available is current page is b/w 1 and total page count
  fullCommentary.hasMore = (page > 1 && page <= pageCount) ? true : false

  // if not initial load append the current page commentary
  if (fullCommentary && fullCommentary.init != true) {
    // merge oversbypage with the previous state
    fullCommentary.oversByPage = Object.assign({}, preOversByPage, oversByPage)

    // compile all the overs from oversbypage
    allOvers = getAllOvers(fullCommentary.oversByPage)

    // update with all overs
    fullCommentary.overs = allOvers.reverse()
  }

  //add lec clips into an array of objects in data
  updateLecClips(state, data, copyRightOptions)

  checkLastEndOfOver(fullCommentary, false)

  return Object.assign({}, state, {
    ...data
  })
}

/**
 * filter overs by page and returns all overs
 * 
 * @param {any} oversByPage overs arranged by page
 * @returns {Array} array of overs
 */
function getAllOvers(oversByPage) {
  let allOvers = []
  let pages
  let pagesFiltered

  pages = Object.keys(oversByPage).sort((a,b) => b-a)

  pagesFiltered = pages.reduce(function (filteredPageNums, currentPage) {
    const length = filteredPageNums.length

    // get the pages in sequence and leave the rest
    if(length === 0 || (filteredPageNums[length-1] - currentPage) === 1) {
      filteredPageNums.push(currentPage)
    }

    return filteredPageNums
  },[])

  pagesFiltered.reverse().map((page) => {
    allOvers = allOvers.concat(oversByPage[page])
  })

  return allOvers
}

/**
 * Add a flag if the over has the last end-of-over
 * 
 * @param {Object} commentary commentary object
 * @param {Boolean} checkReverse decides in which direction to check
 * @return {undefined}
 */
export function checkLastEndOfOver(commentary = {}, checkReverse = false) {
  const overs = commentary.overs || []
  const oversLen = overs.length
  let checked = false

  if (oversLen === 0) {
    return
  }

  var setLastEndOfOver = function setLastEndOfOver(over) {
    if (checked) {
      over.hasLastEndOfOver = false
      return
    }

    if (over.endOfOver) {
      over.hasLastEndOfOver = true
      checked = true
    }
  }

  if (checkReverse) {
    for (let index = oversLen - 1; index >= 0; index--) {
      let over = overs[index] || {}
      setLastEndOfOver(over)
    }
  } else {
    for (let index = 0; index < oversLen; index++) {
      let over = overs[index] || {}
      setLastEndOfOver(over)
    }
  }
}

export function updateVideos(state = {}, data ={}, copyRightOptions = {}) {
  const prevVideos = selectn('allVideos', state) || {}
  const { videos : preVideos = [], page: prevPage = 1 } = prevVideos

  let allVideos = selectn('allVideos', data) || {}
  let { videos = [], hasMore } = allVideos

  if(state.country && videos) {

    //check for copyright video for a country and show/hide
    videos = videos.filter(i => {
      return showCopyrightVideo(i.ad, copyRightOptions)
    })

    videos = videos.filter(v => {
      return !isGeoRestricted(v.geoRestrictions, state.country)
    })
  }

  allVideos.videos = preVideos.concat(videos)

  allVideos.page = hasMore ? (prevPage + 1) : prevPage

  return Object.assign({}, state, {
    ...data
  })
}

/**
 * [updateLecClips update the LEC clips data]
 * @param  {Object} state [current state]
 * @param  {Object} data  [data]
 * @param  {Object} copyRightOptions user's domain, site etc information
 * @return {Object}       [returns updated data]
 */
export function updateLecClips(state = {}, data={}, copyRightOptions = {}) {
  let fullCommentary = selectn('fullCommentary', data) || {}
  let prevLecClips = selectn('lecclips', state) || []
  let lecclips = selectn('lecclips', data) || []
  let { page } = fullCommentary

  if(state.country && lecclips) {

    //check for copyright video for a country and show/hide
    lecclips = lecclips.filter(i => {
      return showCopyrightVideo(i.ad, copyRightOptions)
    })

    lecclips = lecclips.filter(v => {
      return !isGeoRestricted(v.geoRestrictions, state.country)
    })
  }

  // page 1 means on load, otherwise concat content to prev state
  if(page !== 1) {
    lecclips = prevLecClips.concat(lecclips)
  }


  data.lecclips = lecclips

  return Object.assign({}, state, {
    ...data
  })
}
export const filterVideoContent = (data = {}, country = '', copyRightOptions = {}) => {
  let gridVideos = selectn('allVideos.videos', data) || []
  let sliderVideos = selectn('videoSlider.videos', data) || []
  let articleVideos = selectn('videoSlider.articleVideos', data) || []
  let highlightVideos = selectn('videoSlider.highlightVideos', data) || []
  let lecclips = selectn('lecclips', data) || []
  let rosterVideos = selectn('rosterVideos', data) || []
  let matchCoverageArticles = [].concat.apply([], (data.matchCoverage || []).map((a) => {
    return a.articles
  }))
  let links = data.links || []

  //check for copyright video for a country and show/hide
  gridVideos = gridVideos.filter(i => {
    return showCopyrightVideo(i.ad, copyRightOptions)
  })

  sliderVideos = sliderVideos.filter(i => {
    return showCopyrightVideo(i.ad, copyRightOptions)
  })

  articleVideos = articleVideos.filter(i => {
    return showCopyrightVideo(i.ad, copyRightOptions)
  })

  highlightVideos = highlightVideos.filter(i => {
    return showCopyrightVideo(i.ad, copyRightOptions)
  })

  lecclips = lecclips.filter(i => {
    return showCopyrightVideo(i.ad, copyRightOptions)
  })

  rosterVideos = rosterVideos.filter(i => {
    return showCopyrightVideo(i.ad, copyRightOptions)
  })
  // End

  if(country) {
    gridVideos = gridVideos.filter(v => {
      return !isGeoRestricted(v.geoRestrictions, country)
    })
    sliderVideos = sliderVideos.filter(v => {
      return !isGeoRestricted(v.geoRestrictions, country)
    })
    articleVideos = articleVideos.filter(v => {
      return !isGeoRestricted(v.geoRestrictions, country)
    })
    highlightVideos = highlightVideos.filter(v => {		
      return !isGeoRestricted(v.geoRestrictions, country)		
    })
    lecclips = lecclips.filter(v => {
      return !isGeoRestricted(v.geoRestrictions, country)
    })
    rosterVideos = rosterVideos.filter(v => {
      return !isGeoRestricted(v.geoRestrictions, country)
    })
    matchCoverageArticles.forEach(a => {
      if(a.video && isGeoRestricted(a.video.geoRestrictions, country)) {
        a.video = undefined
      }
    })
    // If there are no videos to show, remove video from nav
    if(!gridVideos.length) {
      var videoLinkPos = -1
      let numLinks = links.length
      // Get the position at which video object resides in links array
      for(let i = 0; i < numLinks; i++) {
        if(links[i].pageType === 'video') {
          videoLinkPos = i
          //console.info('videoLinkPos', videoLinkPos)
        }
      }
      if(videoLinkPos !== -1) {
        links.splice(videoLinkPos, 1)
      }
    }
    if(data.allVideos) {
      data.allVideos.videos = gridVideos
    }
    if(data.videoSlider) {
      data.videoSlider.videos = sliderVideos.slice(0, 5) // limit slider videos here
      data.videoSlider.articleVideos = articleVideos.slice(0, 1)
      data.videoSlider.highlightVideos = highlightVideos
    }
    if(data.lecclips) {
      data.lecclips = lecclips
    }
    if(data.rosterVideos) {
      data.rosterVideos = rosterVideos
    }
    if(data.links && data.links.length !== links.length) {
      data.links = links
    }

    filterCommentaryVideos(data.partCommentary, country, copyRightOptions)

  }
}


export function setLocalTime(date) {
  const tzOptions = {
    timeZone : moment.tz.guess(),
    timeFormat : 'hh:mm A'
  }

  return dateFormat.convertDateTime(date, tzOptions)
}

export function filterEvent(state = {}, data = {}, isSBUpdate = true, favs = []) {
  let favTeams = favs.length ? favs : state.favTeams || []
  let topEventBackup = []
  let leaguesBackup = []
  let topEventLeagues = []
  let favLeagues = []
  let leagues = []
  if (isSBUpdate) {
    leaguesBackup = data.leagues || []
    topEventBackup = data.isToday ? state.topEventBackup : []
  } else {
    leaguesBackup = state.leaguesBackup || []
    topEventBackup = data.topEventLeagues || []
  }

  if (favTeams.length) {
    let LeaguefavEvents = []
    let topFavEvents = []
    topEventBackup.length && ({ filteredLeagues: topEventLeagues, favEvents: topFavEvents = [] } = extractFavEvents(topEventBackup, favTeams))

    leaguesBackup.length && ({ filteredLeagues: leagues, favEvents: LeaguefavEvents = [] } = extractFavEvents(leaguesBackup, favTeams))
    if (LeaguefavEvents.length > 0 || topFavEvents.length > 0) {
      favLeagues.push(
        {
          abbreviation: 'Favorites',
          name: 'Favorites',
          id: '',
          seriesHome: '',
          events: [ ...topFavEvents, ...LeaguefavEvents ],
          order: 1,
          isFavorites: true
        })
    }
  } else {
    topEventLeagues = topEventBackup
    leagues = leaguesBackup
  }
  return Object.assign({}, state, {
    ...data,
    favTeams,
    topEventLeagues,
    favLeagues,
    leagues,
    topEventBackup,
    leaguesBackup, 
    loading : false
  })
}

export function extractFavEvents(leagues, favTeams) {
  let favEvents = []
  let filteredLeagues = []
  filteredLeagues = leagues.map((league)=> {
    let { events=[], ...rest } = league
    events = events.filter((ev)=> {
      let awayId = parseInt(selectn('teams.away.id', ev) || 0)
      let homeId = parseInt(selectn('teams.home.id', ev) || 0)
      if(favTeams.indexOf(awayId) !== -1 || favTeams.indexOf(homeId) !== -1) {
        favEvents.push(ev)
        return false
      } else {
        return true
      }
    })
    return {
      events,
      ...rest
    }
  })
  return {
    filteredLeagues,
    favEvents
  }
}
export function updateMidColFeed(state = {}, data ={}) {
  const prevArticles = selectn('middleColumn', state) || {}
  const { newsfeed : preArticles = [], page, resultsCount, excludedCount : excludedCountPrev, geoRestricted : geoRestrictedPrev , totalCount : prevTotalCount } = prevArticles

  let allArticles = selectn('middleColumn', data) || {}
  let { newsfeed = [], excludedCount, geoRestricted, totalCount } = allArticles

  allArticles.newsfeed = preArticles.concat(newsfeed) //merge prev items with new items

  allArticles.hasMore = ((prevTotalCount + totalCount) < resultsCount) && totalCount != 0 ? true : false //decides if there are data remaining to lazy load
  //below datas are cosmetic
  allArticles.page = page + 1
  allArticles.excludedCount = excludedCount + excludedCountPrev
  allArticles.totalCount = totalCount + prevTotalCount
  allArticles.geoRestricted = geoRestricted + geoRestrictedPrev
  allArticles.resultsCount = resultsCount

  return Object.assign({}, state, {
    ...data
  })
}

export function midColVidGeoFilter(data = {}, country, prevState) {
  let prevStateNewsFeed = selectn('middleColumn.newsfeed', prevState)
  let count = ( prevStateNewsFeed !== undefined ) ? prevStateNewsFeed.length : 0
  let feed = selectn('middleColumn.newsfeed', data) || []
  let sport = selectn('middleColumn.sport', data) || ''

  let filteredFeed = feed.filter(v => {
    let geoRestricted = isGeoRestricted(selectn('video.geoRestrictions', v), country)
    if (geoRestricted === false) {
      count = count + 1
      v.tracking = common.createTrackingName([ sport, 'TeamClubhouse', count, 'News' ])
    }
    return !isGeoRestricted(selectn('video.geoRestrictions', v), country)
  })

  data.middleColumn.newsfeed = filteredFeed
  data.middleColumn.geoRestricted = (feed.length - filteredFeed.length)
}

export function filterBet365(bet365 = {}, country) {
  const blacklist = [ 'AU','US','PK','IN','EG','IR','SA','SY','AE','IL','JO','PS','LB','OM','KW','QA','BH','YE','LK','FR','FX','CL','CO','AF','AS','AO','AR','BH','BD','BE','BO','BR','BG','BI','KH','CA','TD','CR','CU','CD','DO','EC','SV','ER','FR','GF','GR','GP','GT','GW','HT','HN','HK','ID','IQ','IT','KW','LY','MY','MQ','MX','MC','NL','NI','OM','PK','PA','PY','PE','PH','PR','QA','RO','BL','MF','SA','SG','SO','ZA','TJ','TR','TM','UY','VI','UZ','VE','YE','ZW','NZ','WW','UNKNOWN' ]

  // apply geo filtering if bet365 set to show in edition
  if (bet365.showInEdition === true) {
    // compute future if valid country code is available else ignore since bet365 is blacklisted by default
    if(typeof country === 'string' && country !== '') {
      country = country.toUpperCase()

      // show bet365 if country is not blacklisted
      if (blacklist.indexOf(country) == -1) {
        bet365.showInGeo = true
      }
    }
  }
}

export function updateNavLinks(navNode, navLinks) {
  const linkMap = {
    'home'     : 'homeLink',
    'fixtures' : 'fixturesLink',
    'results'  : 'resultsLink',
    'players'  : 'playersLink',
    'stats'    : 'statsLink',
    'videos'   : 'videosLink',
    'table'    : 'tableLink' ,
    'squads'   : 'squadsLink',
    'seasons'  : 'seasonsLink'
  }

  let navigation = selectn('subNavMenu.navigation', navNode)
  let items = navigation.items || []

  let homeLink = navLinks['homeLink']

  if (homeLink) {
    navigation.links[0].href = homeLink
    navigation.links[0].text = navLinks.name
  }

  items
    .map((item = {}) => {
      const itemLink = item.links[0] || {}
      let attrText = selectn('attributes.text', itemLink) || ''

      if (attrText && typeof attrText === 'string') {
        attrText = attrText
                    .trim()
                    .toLowerCase()

        const linkKey = linkMap[ attrText ]

        itemLink.href = navLinks[linkKey] || '#'
        itemLink.text = itemLink.shortText || ''
      }
    })


}

/**
 * Update the article
 *
 * @param {Object} article      existing article
 * @param {Object} articleNew   new article
 * @param {Object} pageType     pagetype
 * @param {Boolean} isFastcast  flag to identify if fastcast update
 * @returns {Object}            updated article
 */
export function updateArticle(article = {}, articleNew = {}, pageType = '', isFastcast) {
  var { id, html = '' } = article
  var { id : idNew, type : typeNew = '', html : htmlNew } = articleNew

  if(typeNew && typeof typeNew === 'string') {
    typeNew = typeNew.toLowerCase()

    typeNew = typeNew === 'recap' ? 'report' : typeNew
  }

  // on initial load
  if (!id) {
    return articleNew
  } else if (!idNew) {
    // retain article html if id is null or not changed instead of showing empty page
    return article
  } else if (idNew == id) {
    // check if state don't have valid content then take from action data 
    if(typeof html === 'undefined' || html.trim().length === 0) {
      article = Object.assign({}, article, articleNew)
    }

    return article
  } else if (pageType === 'game' || typeNew === pageType) {
    // if current tab is summary tab or if the article is different and matches current pageType (report tab of day1, day2, etc.)

    // if no html content retain existing article (hack for empty article in fascast update)
    if(typeof htmlNew === 'undefined' && isFastcast === true) {
      return article
    }

    return articleNew
  } else {
    // if the article is different but different from current pageType retain the existing article (hack for empty article in fascast update)
    return article
  }
}
/**
 * update gamestrip time for prematch games based on user timezone
 * @param {String} isoDate isodate from gamestrip
 * @param {string} sport sport from action
 * @param {Boolean} timeValid timeValid
 * @returns {Object}           date and time
 */
export function updateGameStrip(isoDate, sport = 'cricket', timeValid=true ) {
  let timezone = dateFormat.getLocalTimezone()
  let date  = dateFormat.dateFormat( isoDate, 'DD/MM', timezone )
  let time = dateFormat.dateFormat(isoDate, 'h:mm A z', timezone)
  let dateTimeFormat = 'DD/MM - hh:mm A z'
  if(sport == 'cricket') {
    date = timeValid ? dateFormat.dateFormat(isoDate, dateTimeFormat, timezone) : ''
    time = ''
  }
  return { date, time }
}

export function getNoContentText(pageType, edition) {
  return common.getNoContentText(pageType, edition)
}

export default {
  filterVideoContent,
  updateCommentary,
  updateVideos,
  setLocalTime,
  updateMidColFeed,
  midColVidGeoFilter,
  filterBet365,
  updateNavLinks,
  updateArticle,
  updateGameStrip,
  checkLastEndOfOver,
  getNoContentText
}



// WEBPACK FOOTER //
// ../app/reducers/helpers/index.js